import { useCallback } from 'react';
import { useTracking } from '@edreamsodigeo/frontend-core';
import { useFlowTools } from 'flowTools/useFlowTools';
import { useSemSorting } from '../SemSortingProvider';
import { RESULTS_CATEGORY } from '../../TrackingProviders';
import useGetBuyPathDeal, {
  DEAL_WEEKEND,
} from 'clientState/hooks/useGetBuyPathDeal';
import { usePerformedSearchRequest } from 'clientState/providers/SearchRequestProvider';

const ONE_WAY = 'ONE_WAY';

const NOT_APPLY_LABEL = 'NA';

const getPriceAmount = (itinerary): number | string =>
  itinerary ? Math.round(itinerary.price?.amount) : NOT_APPLY_LABEL;

const getPricePosition = (itinerary): number | string =>
  itinerary ? itinerary.priceSortingPosition : NOT_APPLY_LABEL;

const highlightedTypeToLabel = {
  recommended: 'o',
  withBaggage: 'b',
  fastest: 'f',
  cheapest: 'c',
};

const getSelectedItineraryTypeLabel = (itinerary): string => {
  return 'box:' + (highlightedTypeToLabel[itinerary.highlightedType] || '0');
};

const getSelectedItineraryLabel = (itinerary): string => {
  const pricePositionLabel = getPricePosition(itinerary);
  const priceLabel = getPriceAmount(itinerary);
  const typeLabel = getSelectedItineraryTypeLabel(itinerary);
  return [`pos:${pricePositionLabel}`, `pri:${priceLabel}`, typeLabel].join(
    '-'
  );
};

const getRecommendedSelectionLabel = (
  itinerary,
  filterApplied
): Array<string> => {
  return [
    's',
    getSelectedItineraryLabel(itinerary),
    `filter:${filterApplied ? 1 : 0}`,
    'vp:0',
  ];
};

const getItineraryOnClickLabel = (
  itinerary,
  tripType = '',
  position
): Array<string> => {
  return [
    `s_pos:${position + 1}`,
    `pri:${itinerary.price?.amount}`,
    `go:${itinerary.legs?.[0]?.segments?.[0]?.carrier?.id}`,
    itinerary.legs?.[1]?.segments?.[1]?.carrier?.id
      ? `re:${itinerary.legs?.[1]?.segments?.[1]?.carrier?.id}`
      : null,
    `ty:${tripType === ONE_WAY ? 'OW' : 'RT'}`,
  ].filter<string>((a): a is string => typeof a === 'string');
};

const getHighlightedLabel = (highlightedTypeOriginal): number => {
  return highlightedTypeOriginal === 'recommended' ? 0 : 1;
};

const getTrendLabel = (trend): string => {
  if (!trend) return 'so';

  const trends = {
    down: 'ch',
    up: 'ex',
    equal: 'eq',
  };

  return trends[trend];
};

const getTabLabel = (tab): string => {
  const tabs = {
    recom: 'recom',
    price: 'cheap',
    duration: 'fast',
  };

  return tabs[tab];
};

const getDealItineraryOnClickLabel = ({
  action,
  itinerary,
  position,
  tab,
  isFilterApplied,
}): Array<string> => {
  const dealType = action === 'weekend-deals' ? 'wd' : 'hd';

  return [
    action,
    'click',
    `${dealType}pos:${position + 1}`,
    `gd:${getHighlightedLabel(itinerary.dealInfo?.highlightedTypeOriginal)}`,
    `pr:${getTrendLabel(itinerary.dealInfo?.trend)}`,
    `tab:${getTabLabel(tab)}`,
    `filt:${isFilterApplied ? 1 : 0}`,
    `pag:flres`,
  ];
};
export const useIsFilterApplied = (): boolean => {
  //TODO use filters applied in sem
  const appliedFiltersCounter = 0;
  return appliedFiltersCounter > 0;
};

const getFlightNumbers = (itinerary, tripType): string => {
  const selectedSegments = itinerary.legs.map(({ segments }) => segments[0]);
  return selectedSegments.reduce((label, { sections }, index) => {
    const flightCodes = sections.map(({ flightCode }) => flightCode).join(',');
    const tripTypeLabel = tripType === 'multipleTrip' ? `_m${index}:` : `_r:`;
    return !index
      ? `${label}${flightCodes}`
      : `${label}${tripTypeLabel}${flightCodes}`;
  }, 'd:');
};

const useSemItineraryTracking = (): {
  sendTracking: (itinerary: object, position: number) => void;
} => {
  const { trackPage, trackEvent } = useTracking();
  const isFilterApplied = useIsFilterApplied();
  const searchRequest = usePerformedSearchRequest();
  const [sorting] = useSemSorting();
  const { isMobile } = useFlowTools();
  const dealBuyPath = useGetBuyPathDeal();

  const tripType = searchRequest ? searchRequest.tripType : '';
  const sendTracking = useCallback(
    (itinerary, position) => {
      if (isMobile) {
        trackPage('summary', {
          funnel: true,
          customDimensions: {
            dimension61: getFlightNumbers(itinerary, tripType),
          },
        });
      }
      trackEvent({
        category: RESULTS_CATEGORY,
        action: 'searcher_flights',
        label: getItineraryOnClickLabel(itinerary, tripType, position).join(
          '-'
        ),
      });
      trackEvent({
        category: RESULTS_CATEGORY,
        action: 'rec',
        label: getRecommendedSelectionLabel(itinerary, isFilterApplied).join(
          '_'
        ),
      });
      if (itinerary.dealInfo) {
        const action =
          dealBuyPath === DEAL_WEEKEND ? 'weekend-deals' : 'hottest-deals';

        trackEvent({
          category: RESULTS_CATEGORY,
          action: action,
          label: getDealItineraryOnClickLabel({
            action,
            itinerary,
            position,
            tab: sorting,
            isFilterApplied,
          }).join('_'),
        });
      }
    },
    [
      trackPage,
      isFilterApplied,
      trackEvent,
      sorting,
      isMobile,
      dealBuyPath,
      tripType,
    ]
  );
  return { sendTracking };
};

export default useSemItineraryTracking;
